/**
 * In-page anchor scroll with offset for the sticky header (`.header.scrolled` is 80px in
 * `src/user/styles.css`). `scrollIntoView` aligns to the viewport top, so headings sit under the bar.
 *
 * After the preloader, the first scroll can run while layout above the target (fonts, images,
 * sliders) is still settling — `getBoundingClientRect()` is then too small and we underscroll.
 * A later correction pass re-measures after layout has stabilized.
 */
const STICKY_HEADER_SCROLL_OFFSET_PX = 88;
/** Wait for smooth scroll to finish before correcting; avoids fighting the animation */
const LAYOUT_CORRECT_MS = 450;
const CORRECT_THRESHOLD_PX = 8;

function getScrollTopForElement(el: HTMLElement): number {
	const top =
		el.getBoundingClientRect().top + window.scrollY - STICKY_HEADER_SCROLL_OFFSET_PX;
	return Math.max(0, top);
}

function setDocumentScrollTop(top: number, behavior: ScrollBehavior): void {
	window.scrollTo({ top, behavior });
	document.documentElement.scrollTop = top;
	document.body.scrollTop = top;
}

export function scrollToHashAnchor(
	hash: string | undefined | null,
	options: { behavior?: ScrollBehavior } = {}
): void {
	if (!hash) {
		return;
	}
	const id = hash.replace(/^#/, '').trim();
	if (!id) {
		return;
	}

	const behavior = options.behavior ?? 'smooth';

	const run = () => {
		if (id === 'home') {
			setDocumentScrollTop(0, behavior);
			return;
		}

		const el = document.getElementById(id);
		if (!el) {
			return;
		}

		setDocumentScrollTop(getScrollTopForElement(el), behavior);

		window.setTimeout(() => {
			const el2 = document.getElementById(id);
			if (!el2) {
				return;
			}
			const target = getScrollTopForElement(el2);
			if (Math.abs(window.scrollY - target) > CORRECT_THRESHOLD_PX) {
				setDocumentScrollTop(target, 'auto');
			}
		}, LAYOUT_CORRECT_MS);
	};

	// rAF ×2 + microtask: paint after preloader swap; first click then measures stable layout more often
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			setTimeout(run, 0);
		});
	});
}
